// src/pages/admin/CmsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Globe } from 'lucide-react'
import { adminCmsApi } from '../../api/services'
import { Card, CardBody, Button, StatusBadge, PageSpinner, Empty, Pagination } from '../../components/common/ui'
import toast from 'react-hot-toast'

const INIT = { title:'', slug:'', type:'page', body:'', excerpt:'', status:'draft', meta_title:'', meta_desc:'', author_name:'' }

export default function AdminCmsPage() {
  const qc = useQueryClient()
  const [type, setType] = useState('')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(INIT)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const { data, isLoading } = useQuery({ queryKey: ['admin-cms', type, page], queryFn: () => adminCmsApi.list({ type: type||undefined, page }) })
  const save = useMutation({ mutationFn: () => editing === 'new' ? adminCmsApi.store(form) : adminCmsApi.update(editing, form), onSuccess: () => { toast.success('Saved'); qc.invalidateQueries(['admin-cms']); setEditing(null) }, onError: e => toast.error(e.response?.data?.message||'Error') })
  const publish = useMutation({ mutationFn: id => adminCmsApi.publish(id), onSuccess: () => { toast.success('Published'); qc.invalidateQueries(['admin-cms']) } })
  const del = useMutation({ mutationFn: id => adminCmsApi.destroy(id), onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['admin-cms']) } })

  if (isLoading) return <PageSpinner />
  const pages = data?.data?.data?.data || []
  const lastPage = data?.data?.data?.last_page || 1

  const startEdit = (p) => { setEditing(p.id); setForm({ ...INIT, ...p }) }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">CMS Pages</h1>
        <Button size="sm" onClick={() => { setEditing('new'); setForm(INIT) }}><Plus size={14} /> New Page</Button>
      </div>

      <div className="flex gap-2 mb-4">
        {['','page','blog','faq'].map(t => (
          <button key={t} onClick={() => { setType(t); setPage(1) }}
            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${type === t ? 'bg-pink text-white border-pink' : 'border-border text-muted hover:border-pink'}`}>
            {t || 'All'}
          </button>
        ))}
      </div>

      {editing && (
        <Card className="mb-6">
          <CardBody className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <input className="input" placeholder="Title *" value={form.title} onChange={set('title')} />
              <input className="input" placeholder="Slug *" value={form.slug} onChange={set('slug')} />
              <select className="input" value={form.type} onChange={set('type')}>
                {['page','blog','faq'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <input className="input w-full" placeholder="Excerpt" value={form.excerpt} onChange={set('excerpt')} />
            <textarea className="input w-full h-48" placeholder="Body (HTML) *" value={form.body} onChange={set('body')} />
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="input" placeholder="Meta title" value={form.meta_title} onChange={set('meta_title')} />
              <input className="input" placeholder="Author name" value={form.author_name} onChange={set('author_name')} />
            </div>
            <div className="flex gap-2">
              <Button loading={save.isPending} onClick={() => save.mutate()} disabled={!form.title || !form.slug || !form.body}>Save</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {pages.length === 0 && !editing && <Empty icon="📄" title="No pages" action={<Button size="sm" onClick={() => { setEditing('new'); setForm(INIT) }}>Create First</Button>} />}

      <div className="space-y-2">
        {pages.map(p => (
          <Card key={p.id}>
            <CardBody className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2"><span className="font-bold text-sm">{p.title}</span><span className="text-xs px-2 py-0.5 rounded-full bg-surface border border-border">{p.type}</span><StatusBadge status={p.status} /></div>
                <p className="text-xs text-muted">/{p.slug}</p>
              </div>
              <div className="flex gap-2">
                {p.status !== 'published' && <Button size="sm" variant="secondary" loading={publish.isPending} onClick={() => publish.mutate(p.id)}><Globe size={13} /> Publish</Button>}
                <button onClick={() => startEdit(p)} className="p-1.5 rounded-lg hover:bg-surface"><Pencil size={14} className="text-muted" /></button>
                <button onClick={() => del.mutate(p.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
      {lastPage > 1 && <div className="mt-6"><Pagination currentPage={page} lastPage={lastPage} onPageChange={setPage} /></div>}
    </div>
  )
}
